import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRepresentatives } from '@/hooks/use-representatives';
import { RepresentativeFormDialog } from '@/pages/representatives/RepresentativeFormDialog';
import type { Representative } from '@erp/data-access/interfaces';
import {
  Button, Input,
  Popover, PopoverContent, PopoverTrigger,
  cn,
} from '@erp/ui';
import { ChevronDown, Plus, Search, X } from '@/components/icons';

interface RepresentativeSelectProps {
  value: string | null | undefined;
  onChange: (repId: string | null) => void;
  /** Representatives to highlight at the top of the list (e.g. those linked to the current client). */
  preferredIds?: string[];
  placeholder?: string;
  className?: string;
}

export function RepresentativeSelect({
  value, onChange, preferredIds, placeholder, className,
}: RepresentativeSelectProps) {
  const { t } = useTranslation();
  const { data: reps } = useRepresentatives();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const selected = useMemo(
    () => reps?.find((r) => r.id === value) ?? null,
    [reps, value],
  );

  const filtered = useMemo(() => {
    const list = reps ?? [];
    const q = search.toLowerCase();
    const match = (r: Representative) =>
      !q
      || r.name.toLowerCase().includes(q)
      || (r.phone ?? '').toLowerCase().includes(q)
      || (r.email ?? '').toLowerCase().includes(q);

    if (!preferredIds || preferredIds.length === 0) {
      return list.filter(match);
    }
    const pref = new Set(preferredIds);
    const preferred = list.filter((r) => pref.has(r.id) && match(r));
    const others = list.filter((r) => !pref.has(r.id) && match(r));
    return [...preferred, ...others];
  }, [reps, search, preferredIds]);

  const preferredSet = useMemo(
    () => new Set(preferredIds ?? []),
    [preferredIds],
  );

  const handleSelect = (id: string) => {
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(''); }}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              'w-full justify-between font-normal h-11 md:h-8 gap-2',
              !selected && 'text-muted-foreground',
              className,
            )}
          >
            <span className="truncate text-start">
              {selected?.name ?? placeholder ?? t('representatives.pick')}
            </span>
            <span className="flex items-center gap-1 shrink-0">
              {selected && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClear(e as unknown as React.MouseEvent); }
                  }}
                  aria-label={t('common.clearFilters')}
                  className="inline-flex items-center justify-center rounded hover:bg-muted p-0.5 cursor-pointer"
                >
                  <X size={12} className="text-muted-foreground" />
                </span>
              )}
              <ChevronDown size={14} className="text-muted-foreground" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[min(24rem,calc(100vw-2rem))]" align="start">
          <div className="p-2 border-b flex gap-2 items-center">
            <div className="relative flex-1">
              <Search
                size={12}
                className="absolute start-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('common.search')}
                className="h-8 ps-7 text-xs"
                autoFocus
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => setCreateOpen(true)}
              title={t('representatives.newRepresentative')}
              aria-label={t('representatives.newRepresentative')}
            >
              <Plus size={14} />
            </Button>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {!filtered.length ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                {t('common.noResults')}
              </div>
            ) : (
              filtered.map((r, i) => {
                const isPreferred = preferredSet.has(r.id);
                // Show separator between preferred and others
                const prevIsPreferred = i > 0 ? preferredSet.has(filtered[i - 1]!.id) : isPreferred;
                const showSep = preferredSet.size > 0 && prevIsPreferred && !isPreferred;
                return (
                  <div key={r.id}>
                    {showSep && <div className="my-1 border-t" />}
                    <button
                      type="button"
                      onClick={() => handleSelect(r.id)}
                      className={cn(
                        'w-full flex items-center justify-between px-3 py-1.5 text-sm text-start hover:bg-accent transition-colors',
                        r.id === value && 'bg-primary/10',
                      )}
                    >
                      <span className="truncate">{r.name}</span>
                      <span className="text-xs text-muted-foreground ms-2 truncate">
                        {r.phone ?? r.email ?? ''}
                      </span>
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </PopoverContent>
      </Popover>

      <RepresentativeFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(created) => {
          setCreateOpen(false);
          handleSelect(created.id);
        }}
      />
    </>
  );
}
